/* eslint-disable sort-exports/sort-exports */
import { z } from "zod";
import { atom } from "jotai";

import { JotaiTool } from "../../index";
import {
  searchEmbeddedContent,
  processEmbeddingResults,
  createChunkStorageAtom,
  type EmbeddingSearchResult,
} from "../../shared/embedding-utils";

interface SearchResult extends EmbeddingSearchResult {
  fullSchema: Record<string, any>;
}

const ApiSourceEnum = z.enum([
  "chatbots",
  "tables",
  "interfaces",
  "zapguesser",
  "gulliver",
  "canvas",
  "implementation_registry",
  "authentications",
  "agents",
]);

const SearchToolParams = z.object({
  query: z
    .string()
    .describe("The search query to find similar API specs chunks"),
  source: ApiSourceEnum.optional().describe(
    "Optional source filter to limit search to specific API spec sources",
  ),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Searching for APIs...' or 'Finding documentation...'",
    ),
});

export type SearchToolParamsType = z.infer<typeof SearchToolParams>;

async function searchSimilarChunks(
  query: string,
  source?: string,
): Promise<SearchResult[]> {
  return searchEmbeddedContent({
    endpoint: "/api/canvasui/search",
    query,
    source,
    maxResults: 10,
  }) as Promise<SearchResult[]>;
}

export const searchToolDescription = `Searches for the most similar API specs and interface documentation that can help achieve the user's goal.
- query: The search query to find similar API specs and interface chunks - use before making a network request.
- source: Optional filter to search within specific embedding sources.

IMPORTANT: When working with implementations, always use '/implementations-meta/search?term=<search_term>' endpoint with the 'term' parameter.
NEVER use '/implementations' endpoint as it returns ALL implementations and should be avoided.`;

// Atom to store search chunks by hash
export const searchChunksAtom =
  createChunkStorageAtom<SearchResult>("searchChunksAtom");

export const searchToolAtom = atom<JotaiTool<z.infer<typeof SearchToolParams>>>(
  () => ({
    name: "search_tool",
    description: searchToolDescription,
    parameters: SearchToolParams,
    execute: atom(null, async (_get, set, { args: rawArgs }) => {
      let params: SearchToolParamsType;
      try {
        params = SearchToolParams.parse(rawArgs || {});
      } catch (error) {
        console.error(
          "[SEARCH_TOOL] Error parsing arguments for searchTool:",
          error,
        );
        return {
          $error: {
            message: `Error: Invalid arguments provided for search_tool. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
          },
        };
      }

      const { query, source } = params;

      try {
        const results = await searchSimilarChunks(query, source);
        const { chunkMap, summaryResults } = processEmbeddingResults(results);

        set(searchChunksAtom, chunkMap);

        return {
          content: [
            {
              type: "text",
              text: JSON.stringify(summaryResults, null, 2),
            },
          ],
        };
      } catch (error) {
        console.error("[SEARCH_TOOL] Error in searchToolExecute:", error);
        return {
          $error: {
            message: `Error searching similar chunks: ${error instanceof Error ? error.message : String(error)}`,
          },
        };
      }
    }),
  }),
);

searchToolAtom.debugLabel = "searchToolAtom";

/* eslint-enable sort-exports/sort-exports */
